import { create } from 'zustand';
import type { ReactNode } from 'react';

/**
 * Promise tabanlı diyaloglar (Octo.Mobile resolver deseni / Blazor OctoConfirmDialog+OctoShowDialog):
 *   const ok = await dialogs.confirm({ title, message, kind: 'delete' });
 *   await dialogs.error({ message, detail, sql });
 * DialogHost bileşeni uygulama kökünde bir kez mount edilir (App.tsx): OctoLab ve
 * Node görünümü ana kabuğu kullanmıyor, host kabukta olsaydı oralarda çalışmazdı.
 *
 * İstekler SIRAYA girer. Önceden yeni istek ekrandakinin üzerine yazılıyordu: iki ekran aynı
 * anda hata gösterince ilkinin Promise'i hiç çözülmüyor, o ekranın işlemi "çalışıyor" kalıp
 * araç çubuğu kilitleniyordu.
 */
export type ConfirmKind = 'standard' | 'delete';
export interface ConfirmRequest {
  title?: string;
  message: ReactNode;
  kind?: ConfirmKind;
  okText?: string;
  cancelText?: string;
}
export interface ErrorRequest {
  title?: string;
  message: ReactNode;
  detail?: string | null;
  sql?: string | null;
  tone?: 'danger' | 'warning' | 'info';
}

interface Pending<T> {
  id: number;
  resolve: (v: T) => void;
}
type ConfirmEntry = ConfirmRequest & Pending<boolean>;
type ErrorEntry = ErrorRequest & Pending<void>;
interface DialogState {
  /** Ekrandaki onay (sıranın başı). */
  confirm: ConfirmEntry | null;
  /** Ekrandaki hata (sıranın başı). */
  error: ErrorEntry | null;
  confirmQueue: ConfirmEntry[];
  errorQueue: ErrorEntry[];
  openConfirm: (req: ConfirmRequest) => Promise<boolean>;
  openError: (req: ErrorRequest) => Promise<void>;
  resolveConfirm: (v: boolean) => void;
  resolveError: () => void;
}
let seq = 1;
export const useDialogStore = create<DialogState>((set, get) => ({
  confirm: null,
  error: null,
  confirmQueue: [],
  errorQueue: [],
  openConfirm: (req) =>
    new Promise<boolean>((resolve) => {
      const entry = { ...req, id: seq++, resolve };
      set((s) => (s.confirm ? { confirmQueue: [...s.confirmQueue, entry] } : { confirm: entry }));
    }),
  openError: (req) =>
    new Promise<void>((resolve) => {
      const entry = { ...req, id: seq++, resolve };
      set((s) => (s.error ? { errorQueue: [...s.errorQueue, entry] } : { error: entry }));
    }),
  resolveConfirm: (v) => {
    const { confirm: c, confirmQueue } = get();
    set({ confirm: confirmQueue[0] ?? null, confirmQueue: confirmQueue.slice(1) });
    c?.resolve(v);
  },
  resolveError: () => {
    const { error: e, errorQueue } = get();
    set({ error: errorQueue[0] ?? null, errorQueue: errorQueue.slice(1) });
    e?.resolve();
  },
}));

export const dialogs = {
  confirm: (req: ConfirmRequest) => useDialogStore.getState().openConfirm(req),
  error: (req: ErrorRequest) => useDialogStore.getState().openError(req),
};
